import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'sayitnow_post_publish_event';

interface PublishEvent {
  id: string;
  type: 'publish' | 'schedule';
  timestamp: number;
  dismissed: boolean;
  publicSlug?: string;
  recipientName?: string;
}

/**
 * Hook to manage post-publish success card visibility.
 * Uses sessionStorage so the event is cleared on browser close.
 */
export function usePostPublishSuccess() {
  const [showSuccessCard, setShowSuccessCard] = useState(false);
  const [eventId, setEventId] = useState<string | null>(null);
  const [publicSlug, setPublicSlug] = useState<string | null>(null);
  const [recipientName, setRecipientName] = useState<string | null>(null);

  // Check for pending publish event on mount
  useEffect(() => {
    const storedEvent = sessionStorage.getItem(STORAGE_KEY);
    if (storedEvent) {
      try {
        const event: PublishEvent = JSON.parse(storedEvent);
        // Only show if not already dismissed
        if (!event.dismissed) {
          setShowSuccessCard(true);
          setEventId(event.id);
          setPublicSlug(event.publicSlug ?? null);
          setRecipientName(event.recipientName ?? null);
        }
      } catch {
        // Invalid data, clear it
        sessionStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  // Trigger the success card (called after publish/schedule)
  const triggerSuccess = useCallback((
    type: 'publish' | 'schedule' = 'publish',
    slug?: string,
    name?: string,
  ) => {
    const event: PublishEvent = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      type,
      timestamp: Date.now(),
      dismissed: false,
      publicSlug: slug,
      recipientName: name,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(event));
  }, []);

  // Dismiss the success card permanently for this event
  const dismissSuccess = useCallback(() => {
    const storedEvent = sessionStorage.getItem(STORAGE_KEY);
    if (storedEvent) {
      try {
        const event: PublishEvent = JSON.parse(storedEvent);
        event.dismissed = true;
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(event));
      } catch {
        // Just remove it if parsing fails
      }
    }
    sessionStorage.removeItem(STORAGE_KEY);
    setShowSuccessCard(false);
    setEventId(null);
    setPublicSlug(null);
    setRecipientName(null);
  }, []);

  // Clear the success state on navigation (auto-hide without permanent dismiss)
  const clearOnNavigation = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setShowSuccessCard(false);
    setEventId(null);
    setPublicSlug(null);
    setRecipientName(null);
  }, []);

  return {
    showSuccessCard,
    eventId,
    publicSlug,
    recipientName,
    triggerSuccess,
    dismissSuccess,
    clearOnNavigation,
  };
}
